import { useState } from "react";
import { Reveal } from "@/components/Reveal";
import { useLang } from "@/context/LanguageContext";
import { imgSrc } from "@/lib/api";
import { useIsMobile, ExpandPill } from "@/components/MobileCollapse";

const PORTRAIT = "/chisono/ricognizione-mc2026.jpg";
const ACTION = "/chisono/slalom-mc2026.jpg";
const PECHINO_FLAG = "/chisono/portabandiera-beijing2022.jpg";
const MATTARELLA_FLAG = "/chisono/tricolore-quirinale-2021.jpg";
const LOGO_FISI = "/api/files/bertagnolli-site/uploads/b5d6d8fe-72cd-4d7d-a2c4-c3bc0a341d8c.png";
const LOGO_FG = "/api/files/bertagnolli-site/uploads/311acfaf-65e1-4fe7-843e-1e6189572976.png";
const LOGO_AGITOS = "/api/files/bertagnolli-site/uploads/f3a69d34-4134-4428-8d60-ac8650968ebd.png";

const STAT_VALUES = ["124", "17", "13", "11"];

export default function ChiSono() {
  const { t } = useLang();
  const isMobile = useIsMobile();
  const [descOpen, setDescOpen] = useState(false);
  const showFullDesc = !isMobile || descOpen;
  return (
    <section id="chi-sono" className="relative py-14 sm:py-32 overflow-hidden">
      <span className="font-display text-outline pointer-events-none absolute -top-4 right-0 text-[16vw] leading-none select-none hidden lg:block">
        {t("chiSono.outline")}
      </span>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 lg:items-start">
          <div className="lg:col-span-5 relative flex flex-col lg:self-stretch">
            <Reveal x={-60} y={0}>
              <div className="relative">
                <div className="clip-frame overflow-hidden rounded-2xl border border-slate-800/80">
                  <img src={imgSrc(PORTRAIT)} alt={t("chiSono.portraitAlt")} className="w-full aspect-[4/5] object-cover scale-[1.15] origin-[50%_28%]" loading="lazy" />
                </div>
                <p data-testid="chisono-caption-inspect" className="absolute -bottom-7 left-0.5 max-w-[calc(100%-12rem)] sm:max-w-[calc(100%-15rem)] text-[8px] sm:text-[9px] uppercase tracking-[0.14em] text-slate-500 leading-snug">
                  {t("chiSono.photoInspectCaption")}
                </p>
                <div className="absolute -bottom-8 -right-4 sm:-right-8 w-40 sm:w-56 rotate-3">
                  <div className="overflow-hidden rounded-xl border border-amber-400/40 shadow-[0_20px_60px_rgba(0,0,0,0.6)]">
                    <img src={imgSrc(ACTION)} alt={t("chiSono.actionAlt")} className="w-full aspect-square object-cover" loading="lazy" />
                  </div>
                  <p data-testid="chisono-caption-slalom" className="mt-1 text-right text-[8px] uppercase tracking-[0.1em] text-slate-500 leading-snug">
                    {t("chiSono.photoSlalomCaption")}
                  </p>
                </div>
                <span className="font-display absolute -top-5 -left-2 bg-amber-400 text-[#090D14] px-4 py-1.5 text-lg tracking-[0.1em] -rotate-3 z-10">
                  {t("chiSono.badge")}
                </span>
              </div>
            </Reveal>

            <Reveal delay={0.25} className="mt-14 grid grid-cols-2 gap-4">
              <figure className="rotate-[1.5deg] transition-transform duration-500 hover:rotate-0">
                <div className="overflow-hidden rounded-xl border border-slate-800/80 shadow-[0_14px_30px_rgba(0,0,0,0.45)]">
                  <img
                    src={imgSrc(PECHINO_FLAG, 480)}
                    alt={t("chiSono.photoFlagCaption")}
                    loading="lazy"
                    className="w-full aspect-[4/3] object-cover"
                    data-testid="chisono-photo-beijing"
                  />
                </div>
                <figcaption className="mt-2 text-[9px] uppercase tracking-[0.14em] text-slate-500 leading-snug">{t("chiSono.photoFlagCaption")}</figcaption>
              </figure>
              <figure className="-rotate-[1.5deg] mt-5 transition-transform duration-500 hover:rotate-0">
                <div className="overflow-hidden rounded-xl border border-slate-800/80 shadow-[0_14px_30px_rgba(0,0,0,0.45)]">
                  <img
                    src={imgSrc(MATTARELLA_FLAG, 480)}
                    alt={t("chiSono.photoMattarellaCaption")}
                    loading="lazy"
                    className="w-full aspect-[4/3] object-cover"
                    data-testid="chisono-photo-mattarella"
                  />
                </div>
                <figcaption className="mt-2 text-[9px] uppercase tracking-[0.14em] text-slate-500 leading-snug">{t("chiSono.photoMattarellaCaption")}</figcaption>
              </figure>
            </Reveal>

            <Reveal delay={0.2} className="mt-10 lg:mt-auto lg:pt-10">
              <div className="flex items-center justify-center gap-4 sm:gap-8" data-testid="chisono-badges">
                {[
                  { src: LOGO_FISI, label: "FISI", title: t("chiSono.fedFisi"), testid: "chisono-fisi-badge", rot: "-rotate-2", href: "https://www.fisi.org" },
                  { src: LOGO_FG, label: "Fiamme Gialle", title: t("chiSono.fedFg"), testid: "chisono-fiamme-badge", rot: "rotate-2", href: "https://www.gdf.gov.it" },
                  { src: LOGO_AGITOS, label: "Agitos", title: t("chiSono.fedAgitos"), testid: "chisono-agitos-badge", rot: "-rotate-1", href: "https://www.paralympic.org" },
                ].map((b) => (
                  <span key={b.testid} className="flex flex-col items-center gap-2">
                    <a
                      href={b.href}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={b.testid}
                      title={b.title}
                      aria-label={b.title}
                      className={`flex h-20 w-20 sm:h-32 sm:w-32 items-center justify-center rounded-2xl bg-white p-2 sm:p-3 shadow-[0_14px_40px_rgba(0,0,0,0.5)] ${b.rot} transition-all duration-300 hover:rotate-0 hover:scale-105 hover:shadow-[0_18px_50px_rgba(245,158,11,0.25)] focus:outline-none`}
                    >
                      <img src={imgSrc(b.src)} alt={b.title} className="max-h-full max-w-full object-contain" />
                    </a>
                    <span className="text-[9px] uppercase tracking-[0.22em] text-slate-500">{b.label}</span>
                  </span>
                ))}
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-6 lg:col-start-7">
            <Reveal>
              <span className="text-xs uppercase tracking-[0.3em] font-semibold text-sky-400">{t("chiSono.eyebrow")}</span>
              <h2 className="font-display mt-4 text-4xl sm:text-5xl lg:text-6xl text-slate-50 leading-[0.95]">
                {t("chiSono.titleA")}<br />{t("chiSono.titleB1")}<span className="text-amber-400">{t("chiSono.titleAccent")}</span>{t("chiSono.titleB2")}
              </h2>
            </Reveal>
            <Reveal delay={0.12}>
              <p className="mt-7 text-base sm:text-lg text-slate-300 leading-relaxed">
                {t("chiSono.p1")}
              </p>
              {showFullDesc && (
                <>
                  {t("chiSono.body").map((p, i) => (
                    <p key={i} className="mt-4 text-sm sm:text-base text-slate-400 leading-relaxed">
                      {p}
                    </p>
                  ))}
                </>
              )}
              <p data-testid="chisono-closing" className="mt-6 text-base sm:text-lg font-semibold text-amber-400/95 leading-relaxed">
                {t("chiSono.closing")}
              </p>
              {isMobile && (
                <ExpandPill
                  open={descOpen}
                  onToggle={() => setDescOpen((v) => !v)}
                  testid="chisono-expand-description"
                  moreLabel={t("news.readMore")}
                />
              )}
            </Reveal>

            <div className="mt-10 grid grid-cols-2 gap-4">
              {STAT_VALUES.map((value, i) => (
                <Reveal key={value} delay={0.08 * i}>
                  <div className="rounded-2xl border border-slate-800/80 bg-slate-900/50 backdrop-blur-md px-5 py-5 hover:border-amber-400/40 transition-colors duration-300">
                    <span className="font-display text-4xl sm:text-5xl text-amber-400">{value}</span>
                    <p className="mt-1 text-[11px] uppercase tracking-[0.18em] text-slate-400">{t("chiSono.stats")[i]}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
